import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  Stack,
  TablePagination,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { base_url } from "../api/api-url";
import TicketSummary from "../components/common/TicketSummary";
import { decryptText } from "../utility/cryptoUtils";
import { isAdmin, isAdminOrIsUser } from "../utility/isAdmin";

const Dashboard = () => {
  const [status, setStatus] = useState({
    totalCount: 0,
    openCount: 0,
    resolvedCount: 0,
    escalatedCount: 0,
    inProgressCount: 0,
    closedCount: 0,
  });
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25); // Added state for rows per page
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  
  }, [page, rowsPerPage]); 

  const fetchDashboard = async () => {
    console.log(isAdmin());
    if (isAdmin()) {
      try {
        const response = await fetch(
          `${base_url}/dashboard-report?page=${page}&limit=${rowsPerPage}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const result = await response.json();
        const data = result?.data;
        if (data) {
          setStatus(data.statusCount || {});
          setTickets(data.tickets || []);
        }
      } catch (error) {
        console.error("Error fetching dashboard report:", error);
      }
    } else {
      try {
        const response = await fetch(
          `${base_url}/dashboard-report?page=${page}&limit=${rowsPerPage}&department=${decryptText(
            localStorage.getItem("department")
          )}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const result = await response.json();
        const data = result?.data;
        if (data) {
          setStatus(data.statusCount || {});
          setTickets(data.tickets || []);
        }
      } catch (error) {
        console.error("Error fetching dashboard report:", error);
      }
    }
  };

  const getSortableValue = (ticket, key) => {
    switch (key) {
      case "assignedDepartment":
        return ticket.assignedDepartment?.label || "";
      case "assignedTo":
        return ticket.assignedTo.map((p) => p.fullName).join(", ");
      default:
        return ticket[key] || "";
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    const statusMatch = filterStatus
      ? ticket.status?.toLowerCase() === filterStatus.toLowerCase()
      : true;
    const priorityMatch = filterPriority
      ? ticket.priority?.toLowerCase() === filterPriority.toLowerCase()
      : true;
    return statusMatch && priorityMatch;
  });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = getSortableValue(a, sortConfig.key);
    const bVal = getSortableValue(b, sortConfig.key);
    return sortConfig.direction === "asc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  });

  const totalPages = Math.ceil(sortedTickets.length / rowsPerPage);
  const paginatedTickets = sortedTickets.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isAsc = prev.key === key && prev.direction === "asc";
      return { key, direction: isAsc ? "desc" : "asc" };
    });
  };

  const handleNavigate = () => navigate("/reports");

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value); // Update rows per page
    setPage(1); // Reset to first page on rows per page change
  };

  return (
    <Box sx={{ color: "#fff", p: 0, width: "100%" }}>
      <Container
        maxWidth={"100%"}
        sx={{
          margin: "0px",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <TicketSummary handleNavigate={handleNavigate} status={status} />

        {/* Table Card */}
        <Paper
          sx={{
            backgroundColor: "#6c757d",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={3}
            py={1}
            flexWrap="wrap"
            borderBottom="1px solid #333"
          >
            <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
              Recent Tickets
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {/* Status Filter */}
              <FormControl size="small">
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  displayEmpty
                  sx={selectStyles}
                  variant="outlined"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="escalated">Escalated</MenuItem>
                </Select>
              </FormControl>

              {/* Priority Filter */}
              <FormControl size="small">
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  displayEmpty
                  sx={selectStyles}
                  variant="outlined"
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>

              <Button
                size="small"
                variant="outlined"
                sx={{ color: "white", borderColor: "white", height: 35 }}
                onClick={handleNavigate}
              >
                View All →
              </Button>
            </Box>
          </Box>

          {/* Ticket Table */}
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <TableContainer>
              <Table stickyHeader sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Ticket No.", key: "ticketId" },
                      { label: "Dept Issue No.", key: "depTicketId" },
                      { label: "Title", key: "issueTitle" },
                      { label: "Priority", key: "priority" },
                      { label: "Status", key: "status" },
                      { label: "Department", key: "assignedDepartment" },
                      { label: "Assigned To", key: "assignedTo" },
                      { label: "Cluster", key: "cluster" },
                      { label: "Created Date", key: "createdAt" },
                    ].map(({ label, key }) => (
                      <TableCell
                        key={key}
                        sx={headerStyle}
                        onClick={() => handleSort(key)}
                        style={{ cursor: "pointer" }}
                      >
                        {label}{" "}
                        {sortConfig.key === key
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTickets.length > 0 ? (
                    paginatedTickets.map((ticket) => (
                      <TableRow hover key={ticket._id}>
                        <TableCell sx={cellStyle}>
                          {ticket.ticketId || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {ticket.depTicketId || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {ticket.issueTitle || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          <Chip
                            label={ticket.priority || "NA"}
                            size="small"
                            color={getPriorityColor(ticket.priority)}
                          />
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          <Chip
                            label={ticket.status || "NA"}
                            size="small"
                            color={getStatusColor(ticket.status)}
                          />
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {ticket?.assignedDepartment?.label || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {ticket.assignedTo
                            .map((user) => user.fullName)
                            .join(", ") || "Unassigned"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {ticket.client?.cluster || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={cellStyle}>
                        No tickets available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box
            className="mui-wrapper mui-isolated-container p-0"
            sx={{
              color: "white",
              px: 2,
              py: 1,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #ccc",
              backgroundColor: "#8d8787",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={sortedTickets.length}
              rowsPerPage={rowsPerPage}
              page={page - 1} // MUI is 0-based index, your state is 1-based
              onPageChange={(event, newPage) => setPage(newPage + 1)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(1); // Reset to page 1 when changing rows per page
              }}
              sx={{
                color: "white",
                ".MuiTablePagination-toolbar": {
                  m: 0,
                  color: "white",
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                  {
                    color: "white",
                  },
                ".MuiTablePagination-selectIcon": {
                  color: "white",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// --- Styles ---
const headerStyle = {
  backgroundColor: "#333",
  color: "#fff",
  fontWeight: "bold",
};

const cellStyle = {
  color: "#fff",
  backgroundColor: "#1e1e1e",
  borderBottom: "1px solid #333",
};

const selectStyles = {
  color: "white",
  borderColor: "white",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "white",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "white",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
  },
  minWidth: 150,
  height: 35,
};

// --- Chip Color Mapping ---
const getPriorityColor = (priority) => {
  switch ((priority || "").toLowerCase()) {
    case "low":
      return "success";
    case "medium":
      return "warning";
    case "high":
    case "critical":
      return "error";
    default:
      return "default";
  }
};

const getStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "open":
      return "primary";
    case "in progress":
      return "info";
    case "resolved":
      return "success";
    case "closed":
      return "error";
    case "escalated":
      return "error";
    default:
      return "default";
  }
};

export default Dashboard;
